import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AgentsRoster — visual list of all the owner's agents pinned to the top-right
 * column on the home screen, directly underneath SwarmChat. Each row shows:
 *  • the agent's glyph + name (and any nickname tag if set)
 *  • a one-line caption of their job
 *  • CLICK ROW       → fires "hey <name>" through jarvis — they reply in their voice
 *  • ✎ pencil button → opens Agent Studio with this agent pre-selected for editing
 *
 * Owner asked: "Can I have a visual list on my main screen of each agent
 * with a little caption of what their job is, clickable and editable, in
 * little boxes underneath the swarm chat."
 */
export default function AgentsRoster({ onTalkTo, onEdit, onEditVoice }) {
  const [agents, setAgents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Default position: directly under the SwarmChat panel (SwarmChat is at
  // ~y:60 and ~500px tall). Owner asked: "Can I have a visual list ... in
  // little boxes underneath the swarm chat."
  //
  // 🛡 Feb 2026 fix: CommandRail (expanded width 288px / w-72) was overlapping
  // the roster when y > 60 and x < 300. Clamp x to ALWAYS sit east of the rail
  // (+ 12px gap). The clamp also runs on saved-position restore below so an
  // old localStorage value can never drag the box back under the rail.
  const RAIL_RIGHT_EDGE = 300; // 288px rail + 12px gap
  const [pos, setPos] = useState({ x: RAIL_RIGHT_EDGE, y: 580 });
  const dragRef = React.useRef({ active: false, dx: 0, dy: 0 });

  // Save/restore position to localStorage so it persists between sessions
  useEffect(() => {
    try {
      const saved = localStorage.getItem("rawco-agents-roster-pos");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Owner asked multiple times to dock under SwarmChat. If the saved
        // position is the OLD bottom-of-screen default, ignore it so the
        // new "under SwarmChat" default takes effect.
        if (parsed && typeof parsed.y === "number" && parsed.y < window.innerHeight - 100) {
          // 🛡 Clamp x so the roster can never overlap the CommandRail.
          // If the user dragged it under the rail before this fix shipped,
          // snap it back east of the rail.
          const safeX = Math.max(RAIL_RIGHT_EDGE, parsed.x);
          setPos({ x: safeX, y: parsed.y });
        }
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("rawco-agents-roster-pos", JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos]);

  const onMouseDown = (e) => {
    dragRef.current = { active: true, dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      setPos({
        // Clamp left edge to RAIL_RIGHT_EDGE so the user can't drag the
        // roster back under the CommandRail (which was the original
        // overlap complaint).
        x: Math.max(RAIL_RIGHT_EDGE, Math.min(window.innerWidth - 380, e.clientX - dragRef.current.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragRef.current.dy)),
      });
    };
    const onUp = () => { dragRef.current.active = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/jarvis/agents");
      const rows = Array.isArray(r.data) ? r.data : (r.data?.agents || []);
      setAgents(rows.filter(a => a.enabled !== false));
    } catch { /* ignore */ }
    finally { setLoaded(true); }
  };

  useEffect(() => {
    load();
    const onAgentsChanged = () => load();
    window.addEventListener("rawco-agents-changed", onAgentsChanged);
    const id = setInterval(load, 30000); // poll for new agents
    return () => {
      window.removeEventListener("rawco-agents-changed", onAgentsChanged);
      clearInterval(id);
    };
  }, []);

  if (loaded && agents.length === 0) return null;

  return (
    <div
      className="fixed z-[18] pointer-events-auto"
      data-testid="agents-roster"
      style={{
        left: pos.x,
        top: pos.y,
        width: 360,
        maxHeight: "calc(100vh - 120px)",
        overflowY: "auto",
      }}
    >
      <div
        className="rc-panel p-2"
        style={{ background: "rgba(0, 4, 12, 0.94)", border: "1px solid var(--rc-gold-dim)" }}
      >
        <div
          className="flex items-center justify-between px-1 mb-2 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={onMouseDown}
          data-testid="agents-roster-drag-handle"
          title="Drag to move"
        >
          <div className="rc-label text-[9px] tracking-widest">≡ MY AGENTS</div>
          <div className="text-[9px] opacity-50" data-testid="agents-roster-count">{agents.length}</div>
        </div>
        <div className="space-y-1.5">
          {agents.map((a) => {
            const nick = (a.nicknames || [])[0];
            const displayName = a.name || "Agent";
            const caption = a.role || a.purpose || "Sub-agent";
            return (
              <div
                key={a.id}
                data-testid={`agent-roster-row-${a.id}`}
                className="flex items-center gap-2 border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] hover:bg-[rgba(255,184,0,0.06)] transition-all px-2 py-1.5 group"
                title={`Tap 🎙 to change voice · ✎ to edit · 💬 to talk to ${displayName}`}
              >
                <div
                  className="text-base"
                  style={{ width: 22, textAlign: "center", color: "var(--rc-gold)" }}
                >{a.icon || "🤖"}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-display tracking-wider rc-glow-text truncate">
                    {displayName.toUpperCase()}
                    {nick && <span className="text-[9px] opacity-60 ml-1.5 normal-case">aka {nick}</span>}
                  </div>
                  <div className="text-[10px] opacity-70 truncate">{caption}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onTalkTo?.(a); }}
                  data-testid={`agent-roster-talk-${a.id}`}
                  title={`Talk to ${displayName}`}
                  className="text-[var(--rc-text-dim)] hover:text-[var(--rc-gold)] text-xs px-1.5 cursor-pointer"
                >💬</button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEditVoice?.(a); }}
                  data-testid={`agent-roster-voice-${a.id}`}
                  title={`Change ${displayName}'s voice`}
                  className="text-[var(--rc-text-dim)] hover:text-[var(--rc-gold)] text-xs px-1.5 cursor-pointer"
                >🎙</button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEdit?.(a); }}
                  data-testid={`agent-roster-edit-${a.id}`}
                  title={`Edit ${displayName}`}
                  className="text-[var(--rc-text-dim)] hover:text-[var(--rc-gold)] text-xs px-1.5 cursor-pointer"
                >✎</button>
              </div>
            );
          })}
        </div>
        <div className="text-[9px] opacity-40 px-1 pt-2 mt-1 border-t border-[var(--rc-gold-dim)]">
          💬 talk · 🎙 voice · ✎ edit · or say "hey {agents[0]?.name || "Tom"}"
        </div>
      </div>
    </div>
  );
}
